// App-wide data store: fetches the firm config + deals + archive + anti from
// the backend, and exposes mutation helpers that keep local state in sync.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import * as api from "./api";
import type { AppData, Deal, FirmConfig, Screen, TeamMember } from "./types";

export interface UploadJob {
  phase: "working" | "done" | "error";
  deal: Deal | null;
  error: string | null;
}

interface Ctx {
  data: AppData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  upsertDeal: (deal: Deal) => void;
  patchDeal: (id: string, changes: Partial<Deal>) => Promise<Deal>;
  decideDeal: (id: string, payload: api.DecisionPayload) => Promise<Deal>;
  addNote: (id: string, body: string) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;
  saveConfig: (config: FirmConfig) => Promise<void>;
  uploadJob: UploadJob | null;
  startUpload: (email: string, routeId: string, files: File[]) => void;
  clearUpload: () => void;
}

const Context = createContext<Ctx | null>(null);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<AppData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadJob, setUploadJob] = useState<UploadJob | null>(null);
  // "loading" is only true before the very first successful fetch.
  // After that, refresh() updates state in place so individual screens
  // keep their local component state (the Upload result, popovers, etc.).
  const loading = !loaded;

  const refresh = useCallback(async () => {
    try {
      setData(await api.fetchAppData());
      setError(null);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const upsertDeal = useCallback((updated: Deal) => {
    setData((d) => {
      if (!d) return d;
      const inDeals  = d.deals.some(x => x.id === updated.id);
      const inArchive = d.archive.some(x => x.id === updated.id);
      const terminal = ["passed", "invested", "rejected"].includes(updated.status);
      // A terminal deal lives in the archive; everything else in the inbox.
      let deals = d.deals;
      let archive = d.archive;
      if (terminal) {
        deals = deals.filter(x => x.id !== updated.id);
        archive = inArchive
          ? archive.map(x => x.id === updated.id ? updated : x)
          : [updated, ...archive];
      } else {
        archive = archive.filter(x => x.id !== updated.id);
        deals = inDeals
          ? deals.map(x => x.id === updated.id ? updated : x)
          : [updated, ...deals];
      }
      return { ...d, deals, archive };
    });
  }, []);

  const patchDeal = useCallback(async (id: string, changes: Partial<Deal>) => {
    const deal = await api.patchDeal(id, changes);
    upsertDeal(deal);
    return deal;
  }, [upsertDeal]);

  const decideDeal = useCallback(async (id: string, payload: api.DecisionPayload) => {
    const deal = await api.recordDecision(id, payload);
    upsertDeal(deal);
    return deal;
  }, [upsertDeal]);

  const addNote = useCallback(async (id: string, body: string) => {
    await api.addNote(id, body);   // the backend stamps the author
    await refresh();   // notes live on the deal — easiest to re-fetch
  }, [refresh]);

  const deleteDeal = useCallback(async (id: string) => {
    await api.deleteDeal(id);
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        deals: d.deals.filter(x => x.id !== id),
        archive: d.archive.filter(x => x.id !== id),
      };
    });
  }, []);

  const saveConfig = useCallback(async (config: FirmConfig) => {
    const saved = await api.saveFirmConfig(config);
    setData((d) => d ? { ...d, config: saved } : d);
  }, []);

  const startUpload = useCallback((email: string, routeId: string, files: File[]) => {
    setUploadJob({ phase: "working", deal: null, error: null });
    api.processUpload(email, files, routeId).then(async (deal) => {
      await refresh();
      setUploadJob({ phase: "done", deal, error: null });
    }).catch((e: unknown) => {
      setUploadJob({ phase: "error", deal: null, error: String((e as Error).message || e) });
    });
  }, [refresh]);

  const clearUpload = useCallback(() => setUploadJob(null), []);

  const value: Ctx = useMemo(() => ({
    data, loading, error, refresh, upsertDeal,
    patchDeal, decideDeal, addNote, deleteDeal, saveConfig,
    uploadJob, startUpload, clearUpload,
  }), [data, loading, error, refresh, upsertDeal, patchDeal, decideDeal, addNote, deleteDeal, saveConfig, uploadJob, startUpload, clearUpload]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAppData(): Ctx {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useAppData must be used inside <DataProvider>");
  return ctx;
}

/** The signed-in user, available once app data has loaded. */
export function useMe() {
  const { data } = useAppData();
  return data?.me ?? null;
}

/** Resolve a person reference (user id, or legacy initials) to a member. */
export function findMember(
  team: TeamMember[] | undefined,
  ref: string | null | undefined,
): TeamMember | undefined {
  if (!ref || !team) return undefined;
  return team.find((t) => t.id === ref) ?? team.find((t) => t.initials === ref);
}

/** Which screens are gated by which subscription feature flag. */
export const SCREEN_FEATURES: Partial<Record<Screen, string>> = {
  anti: "anti",
  sim: "simulator",
  knowledge: "knowledge",
  enrichments: "enrichments",
  integrations: "integrations",
};

/** Returns a predicate: is this screen enabled for the firm's plan? */
export function useScreenEnabled(): (s: Screen) => boolean {
  const { data } = useAppData();
  const features = data?.firm.features;
  return (s) => {
    const flag = SCREEN_FEATURES[s];
    return !flag || !features || features[flag] !== false;
  };
}
